import { ReactNode } from "react";
import type { TellescopeModelFromName, UsePermissionQueryModel, SupportedSession } from "@tellescope/tanstack-query";
import type { AccessAction } from "@tellescope/types-models";
import { useAppPermissionQuery } from "./use-app-permission-query";
import type { FeatureName } from "./types";

export interface PermissionWrapperProps<Model extends UsePermissionQueryModel> {
  model: Model;
  /** Single action to gate on (adapts to `useAppPermissionQuery`'s `actions`). */
  action: AccessAction;
  /** Plan feature gating this check; defaults from `MODEL_FEATURE_MAP[model]`. */
  feature?: FeatureName;
  record?: TellescopeModelFromName<Model>;
  recordId?: string;
  session?: SupportedSession;
  children: ReactNode;
}

/**
 * Renders `children` only when the current user has `action` access on `model`
 * (combining record permissions and plan/feature availability via
 * {@link useAppPermissionQuery}); renders nothing otherwise.
 */
export const PermissionWrapper = <Model extends UsePermissionQueryModel>({
  children,
  action,
  ...rest
}: PermissionWrapperProps<Model>) => {
  const result = useAppPermissionQuery({ ...rest, actions: [action] });

  if (!result[action].access) {
    return null;
  }

  return children;
};
